import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, Plus, Check, X, Gift } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Badge } from '@/shared/components/ui/Badge';
import { WidgetLoading } from '@/features/dashboard/infrastructure/widgets/WidgetStates';

interface AwardType { _id: string; name: string; category: string; estimatedValueMinor: number; isActive: boolean; }
interface Nomination { _id: string; awardTypeId: { name: string; category: string } | null; recipientUserId: { display_name: string; department: string } | null; recipientName: string; reason: string; status: string; nominatedAt: string; occasion: string; }

function useAwardTypes() { return useQuery({ queryKey: ['rewards', 'types'], queryFn: async () => (await api.get<{ items: AwardType[] }>('/api/rewards/types')).data.items }); }
function useNominations(status?: string) { return useQuery({ queryKey: ['rewards', 'nominations', status], queryFn: async () => { const p = status ? `?status=${status}` : ''; return (await api.get<{ items: Nomination[] }>(`/api/rewards/nominations${p}`)).data.items; } }); }

export function RewardsPage() {
  const qc = useQueryClient();
  const { data: types = [] } = useAwardTypes();
  const { data: nominations = [], isLoading } = useNominations();
  const [showNominate, setShowNominate] = useState(false);
  const [showNewType, setShowNewType] = useState(false);
  const [tab, setTab] = useState<'all' | 'pending' | 'approved' | 'issued'>('all');
  const [form, setForm] = useState({ awardTypeId: '', recipientUserId: '', recipientName: '', reason: '', occasion: '' });
  const [typeForm, setTypeForm] = useState({ name: '', category: 'badge', estimatedValueMinor: '0' });

  const filtered = tab === 'all' ? nominations : nominations.filter(n => n.status === tab);

  const createType = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post('/api/rewards/types', body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rewards'] }); toast.success('Award type created.'); setShowNewType(false); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed.'),
  });

  const nominate = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post('/api/rewards/nominations', body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rewards'] }); toast.success('Nomination submitted.'); setShowNominate(false); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed.'),
  });

  const action = useMutation({
    mutationFn: async ({ id, act, reason }: { id: string; act: string; reason?: string }) => (await api.post(`/api/rewards/nominations/${id}/${act}`, { reason })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rewards'] }); toast.success('Done.'); },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed.'),
  });

  const statusTone = (s: string) => s === 'pending' ? 'warning' : s === 'approved' ? 'info' : s === 'issued' ? 'success' : s === 'rejected' ? 'danger' : 'neutral';

  return (
    <div className="space-y-6 max-w-[1580px] mx-auto pb-16">
      <PageHeader icon={Award} eyebrow="People" title="Recognition & Rewards" description={<>Nominate, approve, and issue non-cash awards to employees.</>} actions={<><div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowNewType(true)}><Gift className="h-4 w-4" /> New Award Type</Button>
          <Button onClick={() => setShowNominate(true)}><Plus className="h-4 w-4" /> Nominate</Button>
        </div></>} />

      {/* Award Types */}
      {types.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {types.map(t => (
            <div key={t._id} className="px-3 py-1.5 rounded-full border text-body-sm font-medium bg-surface-variant/30">
              {t.name} <span className="text-on-surface-variant">({t.category})</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-outline pb-2">
        {(['all', 'pending', 'approved', 'issued'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 text-body-sm font-semibold rounded-lg ${tab === t ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-variant'}`}>
            {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)} ({tab === 'all' ? nominations.length : nominations.filter(n => n.status === t).length})
          </button>
        ))}
      </div>

      {/* Nominations List */}
      {isLoading ? <div className="p-4"><WidgetLoading variant="table" /></div> : filtered.length === 0 ? (
        <p className="text-center py-12 text-on-surface-variant">No nominations found.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(n => (
            <Card key={n._id}>
              <CardBody className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-on-surface">{n.recipientUserId?.display_name || n.recipientName}</span>
                    <Badge tone={statusTone(n.status)}>{n.status}</Badge>
                    {n.occasion && <span className="text-body-sm text-on-surface-variant">· {n.occasion}</span>}
                  </div>
                  <p className="text-body-sm text-on-surface-variant truncate">{n.reason}</p>
                  <p className="text-body-sm text-on-surface-variant mt-0.5">Award: {n.awardTypeId?.name || '?'} · {new Date(n.nominatedAt).toLocaleDateString()}</p>
                </div>
                {n.status === 'pending' && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" onClick={() => action.mutate({ id: n._id, act: 'approve' })}><Check className="h-3.5 w-3.5" /> Approve</Button>
                    <Button size="sm" variant="danger" onClick={() => { const r = window.prompt('Rejection reason:'); if (r) action.mutate({ id: n._id, act: 'reject', reason: r }); }}><X className="h-3.5 w-3.5" /> Reject</Button>
                  </div>
                )}
                {n.status === 'approved' && (
                  <Button size="sm" onClick={() => action.mutate({ id: n._id, act: 'issue' })}><Gift className="h-3.5 w-3.5" /> Issue</Button>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Nominate Modal */}
      <Modal open={showNominate} onClose={() => setShowNominate(false)} title="Nominate for Award" footer={<><Button variant="ghost" onClick={() => setShowNominate(false)}>Cancel</Button><Button onClick={() => nominate.mutate(form)} loading={nominate.isPending}>Submit Nomination</Button></>}>
        <div className="grid gap-3 md:grid-cols-2">
          <Select label="Award Type *" value={form.awardTypeId} onChange={e => setForm(f => ({ ...f, awardTypeId: e.target.value }))} options={[{ value: '', label: 'Select award...' }, ...types.map(t => ({ value: t._id, label: `${t.name} (${t.category})` }))]} />
          <Input label="Recipient Name *" value={form.recipientName} onChange={e => setForm(f => ({ ...f, recipientName: e.target.value }))} placeholder="Employee name" />
          <Input label="Recipient User ID" value={form.recipientUserId} onChange={e => setForm(f => ({ ...f, recipientUserId: e.target.value }))} placeholder="MongoDB ObjectId" />
          <Input label="Occasion" value={form.occasion} onChange={e => setForm(f => ({ ...f, occasion: e.target.value }))} placeholder="e.g. Q2 Best Performer" />
          <div className="md:col-span-2"><label className="text-body-md font-medium">Reason *</label><textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} className="w-full rounded border border-outline bg-surface px-3 py-2 text-data-md" placeholder="Why does this person deserve this award?" /></div>
        </div>
      </Modal>

      {/* New Award Type Modal */}
      <Modal open={showNewType} onClose={() => setShowNewType(false)} title="New Award Type" footer={<><Button variant="ghost" onClick={() => setShowNewType(false)}>Cancel</Button><Button onClick={() => createType.mutate({ name: typeForm.name, category: typeForm.category, estimatedValueMinor: Number(typeForm.estimatedValueMinor) || 0 })} loading={createType.isPending}>Create</Button></>}>
        <div className="grid gap-3">
          <Input label="Award Name *" value={typeForm.name} onChange={e => setTypeForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Star Performer Badge" />
          <Select label="Category" value={typeForm.category} onChange={e => setTypeForm(f => ({ ...f, category: e.target.value }))} options={[{ value: 'badge', label: 'Badge' }, { value: 'certificate', label: 'Certificate' }, { value: 'voucher', label: 'Voucher' }, { value: 'prize', label: 'Prize' }, { value: 'trip', label: 'Trip' }, { value: 'apparel', label: 'Apparel' }, { value: 'other', label: 'Other' }]} />
          <Input label="Estimated Value (paise)" type="number" value={typeForm.estimatedValueMinor} onChange={e => setTypeForm(f => ({ ...f, estimatedValueMinor: e.target.value }))} placeholder="0 = no monetary value" />
        </div>
      </Modal>
    </div>
  );
}
